import * as React from 'react';
import {Redirect, Route, Switch, useHistory, useLocation} from 'react-router-dom';
import {Header, triggerCCEvent} from '@pluto-tv/lib-command-center-ts';
import {Cover, Template, Box, Cluster, Icon, Breadcrumb, Paragraph, Popover, Stack, Sidebar} from '@pluto-tv/assemble';

import NotFound from 'views/notFound/NotFound';
import {getRoutesForUserPermissions, IRouteItem, TRouteCollection} from 'routes';

import AppSidebar from 'app/appSidebar/AppSidebar';
import ErrorBoundary from 'components/errorBoundary';

import {useMeQuery} from 'features/users/usersApi';
import {useInfoQuery as useBackendInfoQuery} from 'features/backendInfo/backendInfoApi';

import {getCCVersionItems, isCommandCenter} from 'helpers/isCommandCenter';
import {useCurrentRoute} from 'helpers/useCurrentRoute';

import {version, dependencies} from '../../../package.json';

export interface IRouterProps {
  userRoutes: TRouteCollection;
}

export interface IRouterPropsFull {
  hasRoles: boolean;
}

export interface IRouterPropsExtra extends IRouterProps {
  currentRoute?: IRouteItem;
  currentRouteExact?: IRouteItem;
}

// Command Center breadcrumb item interface
// as it is not exported from lib-command-center-ts
interface ICCBreadcrumb {
  name: string;
  onClickAction?: () => void;
}

const assembleVersion = dependencies['@pluto-tv/assemble'];

const AppRouterSwitch = React.memo(({userRoutes}: IRouterProps): JSX.Element => {
  return (
    <Switch>
      {userRoutes.map(route => {
        if (route.children) {
          return route.children.map(r => (
            <Route key={r.name} path={r.path} component={r.component} exact={r.exact || true} />
          ));
        }
        return <Route key={route.name} path={route.path} component={route.component} exact={route.exact || true} />;
      })}
      <Route component={NotFound} />
    </Switch>
  );
});

// Last line of defence: without it any uncaught render error unmounts the whole app and leaves a
// blank gray page. Keyed on the pathname so moving to another page clears the error.
const AppRouterContent = ({userRoutes}: IRouterProps): JSX.Element => {
  const {pathname} = useLocation();

  return (
    <ErrorBoundary label='This page' resetKey={pathname}>
      <AppRouterSwitch userRoutes={userRoutes} />
    </ErrorBoundary>
  );
};

export const AppRouterFull = ({userRoutes}: IRouterProps): JSX.Element => {
  return (
    <Cover scrolling={true} coverTemplateHeight='100%' maxWidth='100%'>
      <Template label='cover'>
        <AppRouterContent userRoutes={userRoutes} />
      </Template>
    </Cover>
  );
};

const AppRouter = ({userRoutes, currentRouteExact, currentRoute}: IRouterPropsExtra): JSX.Element => {
  const history = useHistory();
  const location = useLocation();

  const {data: backendInfo} = useBackendInfoQuery();

  const breadcrumbNav = React.useCallback(route => history.push(route), [history]);
  const breadcrumbItems = React.useMemo(
    () => (currentRouteExact || currentRoute)?.breadcrumb?.items,
    [currentRoute, currentRouteExact],
  );

  React.useEffect(() => {
    if (isCommandCenter) {
      const decodedUrl = decodeURIComponent(location.search);
      const params = new URLSearchParams(decodedUrl);

      if (params.has('next')) {
        const nextValue = params.get('next');
        if (nextValue) {
          history.push(nextValue);
        }
      }
    }
  }, [history, location.search]);

  React.useEffect(() => {
    if (isCommandCenter) {
      if (backendInfo?.app?.version && breadcrumbItems) {
        Header().init();
      }

      const breadcrumbData = {
        onClick: () => (window.location.href = `/cms/`),
        crumbs: breadcrumbItems?.map(item => {
          const bc: ICCBreadcrumb = {name: item.text};
          const lastBreadcrumbIndex = (breadcrumbItems || []).length - 1;
          if (item?.route !== breadcrumbItems[lastBreadcrumbIndex].route) {
            bc.onClickAction = () => breadcrumbNav(item.route);
          }
          return bc;
        }),
      };

      triggerCCEvent('setAppVersioning', getCCVersionItems(backendInfo));
      triggerCCEvent('updateHeaderBreadcrumb', breadcrumbData);
    } else {
      const ccSharedHeader = document.getElementById('cc-shared-header');

      if (ccSharedHeader) {
        ccSharedHeader.style.display = 'none';
      }
    }
  }, [backendInfo, breadcrumbItems, breadcrumbNav, location.pathname]);

  return (
    <Cover scrolling={true} coverTemplateHeight='100%' maxWidth='100%'>
      {!isCommandCenter && (
        <Template label='header'>
          <Box paddingX='large' paddingY='small' background='black' hideAt='mobile'>
            <Cluster justify='space-between' align='center'>
              {breadcrumbItems && <Breadcrumb onRoute={breadcrumbNav} items={breadcrumbItems}></Breadcrumb>}
              <Popover allowedPlacements={['bottom-end', 'top-end']}>
                <Template label='trigger'>
                  <Icon
                    icon='arrowdown'
                    space='xxsmall'
                    iconPosition='right'
                    verticalAlign='text-bottom'
                    size='xsmall'
                    lineHeight='0.8125rem'
                  >
                    Versions
                  </Icon>
                </Template>
                <Template label='popover'>
                  <Box padding='xsmall'>
                    <Stack space='medium'>
                      <Paragraph id='clientVersionNumber'>App Version: {version}</Paragraph>
                      <Paragraph id='apiVersionNumber'>API Version: {backendInfo?.app?.version}</Paragraph>
                      <Paragraph id='assembleVersionNumber'>Assemble Version: {assembleVersion}</Paragraph>
                    </Stack>
                  </Box>
                </Template>
              </Popover>
            </Cluster>
          </Box>
        </Template>
      )}
      <Template label='cover'>
        <AppRouterContent userRoutes={userRoutes} />
      </Template>
    </Cover>
  );
};

export default ({hasRoles}: IRouterPropsFull): JSX.Element => {
  const location = useLocation();
  const {data: userProfile} = useMeQuery();

  const userRoutes = React.useMemo(
    (): TRouteCollection => getRoutesForUserPermissions(userProfile?.permissions || []),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [userProfile, location.pathname],
  );

  const {currentRoute, currentRouteExact} = useCurrentRoute(userRoutes);

  const isFullscreen = React.useMemo(
    () => currentRouteExact?.fullscreen || currentRoute?.fullscreen,
    [currentRoute, currentRouteExact],
  );

  if (!hasRoles && location.pathname !== '/') {
    return <Redirect to='/' />;
  }

  if (isFullscreen) {
    return <AppRouterFull userRoutes={userRoutes} />;
  }

  if (!hasRoles) {
    return <AppRouter userRoutes={userRoutes} currentRoute={currentRoute} currentRouteExact={currentRouteExact} />;
  }

  return (
    <Sidebar fullHeight={true} backgroundSidebar='cavern' contentMinWidth='25rem' sideWidth='3rem' stackAt='mobile'>
      <>
        <div style={{height: '100%', minHeight: 0}}>
          <AppSidebar hasRoles={hasRoles} userRoutes={userRoutes} />
        </div>
        <AppRouter userRoutes={userRoutes} currentRoute={currentRoute} currentRouteExact={currentRouteExact} />
      </>
    </Sidebar>
  );
};
